import { useState } from "react";
import { Link } from "react-router-dom";

import { ErrorRow, SettingsRow, usd, useAction, wholeNumber } from "./parts";
import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Segmented } from "../../components/Segmented";
import { Select } from "../../components/Select";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import {
  type ApiCloudRunnerSettings,
  type ApiCloudRunnerSize,
  type RunnerMode,
  updateRunnerSettings,
  useRunnerSettings,
} from "../../data/api/runnerSettings";
import { cx } from "../../lib/cx";
import { paths } from "../../routing/paths";

// Settings → Runners (specifications/cloud-runners.md): self-hosted runners the
// workspace enrolls, or cloud runners Tinsmith launches and bills per
// runner-hour.

const MODE_OPTS = [
  { label: "Self-hosted", value: "self_hosted" },
  { label: "Cloud", value: "cloud" },
];

export const Runners = () => {
  const { isAdmin, workspaceId } = useCurrentWorkspace();
  const { refetch, settings } = useRunnerSettings(workspaceId);

  if (!settings || !workspaceId) {
    return null;
  }
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Runners</h3>
      {settings.cloud ? (
        <ModeCard
          cloud={settings.cloud}
          isAdmin={isAdmin}
          mode={settings.mode}
          onSaved={refetch}
          workspaceId={workspaceId}
        />
      ) : (
        <div className={styles.card}>
          <div className={styles.row}>
            <SelfHostedLine />
          </div>
        </div>
      )}
    </section>
  );
};

const SelfHostedLine = () => (
  <span className={cx(styles.grow, styles.help)}>
    Agents run on your own machines, enrolled on the{" "}
    <Link className={styles.link} to={paths.runners()}>
      Runners page
    </Link>
    . They aren&apos;t metered.
  </span>
);

type RunnersForm = {
  readonly idleMinutes: string;
  readonly maxRunners: string;
  readonly mode: RunnerMode;
  readonly sandboxes: string;
  readonly size: string;
};

const formOf = (
  mode: RunnerMode,
  cloud: ApiCloudRunnerSettings,
): RunnersForm => ({
  idleMinutes: String(cloud.idle_minutes),
  maxRunners: String(cloud.max_runners),
  mode,
  sandboxes: String(cloud.sandboxes_per_runner),
  size: cloud.size,
});

const sizeOption = (size: ApiCloudRunnerSize) => ({
  label: `${size.label} · ${size.vcpus} vCPU · ${size.memory_gb} GB · ${usd(size.usd_per_hour)} an hour`,
  value: size.key,
});

const costLine = (
  size: ApiCloudRunnerSize,
  runners: number,
  idleMinutes: number,
): string =>
  `Up to ${usd(size.usd_per_hour * runners)} an hour with ${runners === 1 ? "1 runner" : `all ${runners} runners`} up. Runners start as tasks need them and stop after ${idleMinutes === 1 ? "1 idle minute" : `${idleMinutes} idle minutes`}.`;

// Not on the page SaveBar: a sensitive write to its own endpoint, like auto
// top-up.
const ModeCard = ({
  cloud,
  isAdmin,
  mode,
  onSaved,
  workspaceId,
}: {
  readonly cloud: ApiCloudRunnerSettings;
  readonly isAdmin: boolean;
  readonly mode: RunnerMode;
  readonly onSaved: () => void;
  readonly workspaceId: string;
}) => {
  const { busy, error, run } = useAction();
  // Null tracks the server; an object is an unsaved edit.
  const [draft, setDraft] = useState<RunnersForm | null>(null);
  const saved = formOf(mode, cloud);
  const form = draft ?? saved;
  const edit = (patch: Partial<RunnersForm>): void =>
    setDraft({ ...form, ...patch });

  const size = cloud.sizes.find((s) => s.key === form.size);
  const sandboxes = size
    ? wholeNumber(form.sandboxes, 1, size.max_sandboxes)
    : null;
  const maxRunners = wholeNumber(form.maxRunners, 1, cloud.max_runners_limit);
  const idleMinutes = wholeNumber(
    form.idleMinutes,
    1,
    cloud.idle_minutes_limit,
  );
  // Checked in either mode: self-hosted still sends, and keeps, the cloud
  // fields.
  const valid =
    size !== undefined &&
    sandboxes !== null &&
    maxRunners !== null &&
    idleMinutes !== null;
  const dirty =
    draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  const canSave = isAdmin && !busy && dirty && valid;

  // A smaller size holds fewer sandboxes; clamp rather than leave the field
  // invalid.
  const pickSize = (key: string): void => {
    const next = cloud.sizes.find((s) => s.key === key);
    edit({
      sandboxes:
        next && Number(form.sandboxes) > next.max_sandboxes
          ? String(next.max_sandboxes)
          : form.sandboxes,
      size: key,
    });
  };

  const save = (): void => {
    if (!canSave) {
      return;
    }
    run(
      () =>
        updateRunnerSettings(workspaceId, {
          idle_minutes: idleMinutes,
          max_runners: maxRunners,
          mode: form.mode,
          sandboxes_per_runner: sandboxes,
          size: size.key,
        }).then(() => {
          setDraft(null);
          onSaved();
        }),
      "Couldn't save runner settings.",
    );
  };

  return (
    <div className={styles.card}>
      <SettingsRow label="Run agents on">
        <Segmented
          aria-label="Runner mode"
          disabled={!isAdmin}
          onValueChange={(next) => edit({ mode: next as RunnerMode })}
          options={MODE_OPTS}
          value={form.mode}
        />
      </SettingsRow>
      {form.mode === "cloud" ? (
        <>
          <SettingsRow label="Size">
            <Select
              disabled={!isAdmin}
              onValueChange={pickSize}
              options={cloud.sizes.map(sizeOption)}
              value={form.size}
            />
          </SettingsRow>
          <SettingsRow
            help={
              size
                ? `How many tasks share a runner, up to ${size.max_sandboxes}.`
                : undefined
            }
            label="Sandboxes per runner"
          >
            <Input
              aria-label="Sandboxes per runner"
              className={styles.numberInput}
              disabled={!isAdmin}
              inputMode="numeric"
              max={size?.max_sandboxes}
              min={1}
              onChange={(e) => edit({ sandboxes: e.target.value })}
              step={1}
              type="number"
              value={form.sandboxes}
            />
          </SettingsRow>
          <SettingsRow
            help={`The most running at once, up to ${cloud.max_runners_limit}.`}
            label="Maximum runners"
          >
            <Input
              aria-label="Maximum runners"
              className={styles.numberInput}
              disabled={!isAdmin}
              inputMode="numeric"
              max={cloud.max_runners_limit}
              min={1}
              onChange={(e) => edit({ maxRunners: e.target.value })}
              step={1}
              type="number"
              value={form.maxRunners}
            />
          </SettingsRow>
          <SettingsRow
            help={`How long an empty runner waits for the next task before it stops, up to ${cloud.idle_minutes_limit} minutes. Its idle time is billed.`}
            label="Idle timeout (minutes)"
          >
            <Input
              aria-label="Idle timeout in minutes"
              className={styles.numberInput}
              disabled={!isAdmin}
              inputMode="numeric"
              max={cloud.idle_minutes_limit}
              min={1}
              onChange={(e) => edit({ idleMinutes: e.target.value })}
              step={1}
              type="number"
              value={form.idleMinutes}
            />
          </SettingsRow>
        </>
      ) : null}
      <div className={styles.row}>
        {form.mode === "cloud" ? (
          <span className={cx(styles.grow, styles.help)}>
            {size && maxRunners !== null && idleMinutes !== null
              ? costLine(size, maxRunners, idleMinutes)
              : null}
          </span>
        ) : (
          <SelfHostedLine />
        )}
        {isAdmin ? (
          <Button disabled={!canSave} onClick={save} size="sm" variant="accent">
            Save
          </Button>
        ) : null}
      </div>
      {isAdmin && !valid ? (
        <ErrorRow
          message={
            size
              ? `Use 1–${size.max_sandboxes} sandboxes per runner, 1–${cloud.max_runners_limit} runners and 1–${cloud.idle_minutes_limit} idle minutes.`
              : "Choose a size."
          }
        />
      ) : null}
      <ErrorRow message={error} />
    </div>
  );
};
